import React, { useCallback } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { useReadableMembershipInfo } from '@components/TownPageLayout/useReadableMembershipInfo'
import { useMembershipSubscriptionsStore } from '@components/Web3/Subscriptions/membershipSubscriptions.store'
import { trackRenewMembership } from '@components/Web3/Subscriptions/subscriptionAnalytics'
import { readableDuration } from '@components/Web3/Subscriptions/SubscriptionsPanel'
import { ACTION_REJECTED, mapToErrorMessage } from '@components/Web3/utils'
import { RenewMembershipParams } from '@towns/userops'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { useAutoRenewalSubscription } from 'hooks/useAutoRenewalSubscription'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { GetSigner } from 'privy/WalletReady'
import { Subscription } from 'stackup-worker'
import {
    blockchainQueryKeys,
    queryClient,
    SignerUndefinedError,
    SpaceAddressFromSpaceId,
    useBaseReads,
    useMyUserId,
    useRenewMembership,
    useSpaceDataStore,
} from 'use-towns-client'
import { isAddress } from 'viem'

import { useMyMembershipStatus } from './useMyMembershipStatus'

export function useRenewMyMembership(args: {
    spaceId?: string
    onSuccess?: () => void
    location: 'reminder' | 'subscriptions' | 'town-page'
}) {
    const { spaceId: spaceIdIn, onSuccess, location } = args
    const spaceIdFromPathname = useSpaceIdFromPathname()
    const spaceId = spaceIdIn ?? spaceIdFromPathname
    const myUserId = useMyUserId()
    const { data: membershipInfo } = useReadableMembershipInfo(spaceId ?? '')
    const { data: membershipStatus, refetch: refetchMembershipStatus } = useMyMembershipStatus({
        spaceId,
    })
    const { renewMembership, ...rest } = useRenewMembership()
    const { data: myAddress } = useMyAbstractAccountAddress()
    const spaceName = useSpaceDataStore((state) =>
        spaceId ? state.spaceDataMap?.[spaceId]?.name : undefined,
    )
    const { getSingleSubscription } = useBaseReads()
    const removeFailedSubscription = useMembershipSubscriptionsStore(
        (s) => s.removeFailedSubscription,
    )
    const addFailedSubscription = useMembershipSubscriptionsStore((s) => s.addFailedSubscription)

    const { installAutoRenewal } = useAutoRenewalSubscription({
        onSuccess: ({ spaceAddress, tokenId }) => {
            removeFailedSubscription(spaceAddress, tokenId)
        },
        onError: ({ spaceAddress, tokenId, error }) => {
            console.error(`[useRenewMyMembership] error installing auto-renewal`, error)
            addFailedSubscription(spaceAddress, tokenId, error.message)
        },
    })

    const _trackRenewMembership = useCallback(
        (success: boolean) => {
            trackRenewMembership({
                spaceName: spaceName ?? '',
                spaceId: spaceId ?? '',
                duration: readableDuration(membershipInfo?.duration ?? 0),
                success,
                location,
            })
        },
        [spaceName, spaceId, membershipInfo?.duration, location],
    )

    return {
        ...rest,
        renewMembership: useCallback(
            async (getSigner: GetSigner, source: string) => {
                const signer = await getSigner()
                if (!signer) {
                    throw new SignerUndefinedError()
                }
                if (!spaceId) {
                    throw new Error('Space ID is required')
                }
                if (!membershipStatus?.tokenId) {
                    throw new Error('Membership token ID is required')
                }
                if (!myAddress) {
                    throw new Error('No abstract account address found')
                }

                const spaceAddress = SpaceAddressFromSpaceId(spaceId)

                if (!isAddress(spaceAddress)) {
                    throw new Error('Invalid space address')
                }

                let subscriptionConfig: RenewMembershipParams['subscriptionConfig'] | undefined
                let currentSubscription: Subscription | null = null

                try {
                    currentSubscription = await getSingleSubscription({
                        walletAddress: myAddress,
                        spaceAddress,
                        tokenId: membershipStatus.tokenId,
                    })

                    // allow batched ops when a sub is already installed (renew + activate)
                    if (currentSubscription && !currentSubscription.active) {
                        subscriptionConfig = {
                            activate: true,
                            entityId: Number(currentSubscription.entityId),
                        }
                    }
                } catch (error) {
                    console.error('[useRenewMyMembership]::error getting subscription', error)
                }

                renewMembership({
                    spaceId,
                    tokenId: membershipStatus.tokenId,
                    userId: myUserId ?? '',
                    signer,
                    subscriptionConfig,
                    onSuccess: async () => {
                        try {
                            _trackRenewMembership(true)

                            const freshData = await refetchMembershipStatus()
                            renewalToasts.success(
                                getSuccessToastDetails({
                                    spaceName,
                                    expiryTime: freshData.data?.expiryTime,
                                }),
                            )
                            if (!currentSubscription && membershipStatus.tokenId) {
                                await installAutoRenewal({
                                    spaceId,
                                    tokenId: membershipStatus.tokenId,
                                    signer,
                                })
                            }

                            if (myAddress && membershipStatus.tokenId) {
                                queryClient.invalidateQueries({
                                    queryKey: blockchainQueryKeys.subscriptions(myAddress),
                                })
                                queryClient.invalidateQueries({
                                    queryKey: blockchainQueryKeys.singleSubscription(
                                        myAddress,
                                        spaceAddress,
                                        membershipStatus.tokenId,
                                    ),
                                })
                            }
                        } catch (error) {
                            console.error('[useRenewMyMembership]::onSuccess', error)
                            renewalToasts.success(`Renewal successful!`)
                        }
                        onSuccess?.()
                    },
                    onError: (error) => {
                        _trackRenewMembership(false)
                        renewalToasts.error(error, source)
                    },
                })
            },
            [
                spaceId,
                membershipStatus?.tokenId,
                myAddress,
                renewMembership,
                myUserId,
                getSingleSubscription,
                onSuccess,
                refetchMembershipStatus,
                spaceName,
                installAutoRenewal,
                _trackRenewMembership,
            ],
        ),
    }
}

function getSuccessToastDetails(args: {
    spaceName: string | undefined
    expiryTime: bigint | undefined
}) {
    const { spaceName, expiryTime } = args
    const formattedExpiryDate = expiryTime
        ? new Date(Number(expiryTime) * 1000).toLocaleDateString(undefined, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
          })
        : ''
    return `Renewal successful! Your membership to ${spaceName} is now valid until ${formattedExpiryDate}.`
}

export const renewalToasts = {
    success: async (message: string) => {
        return popupToast(({ toast }) => {
            return <StandardToast.Success toast={toast} message={message} />
        })
    },
    error: (error: Error, source: string) => {
        const errorMessage = mapToErrorMessage({
            error,
            source,
        })

        if (errorMessage === ACTION_REJECTED) {
            return
        }

        popupToast(({ toast }) => {
            return (
                <StandardToast.Error
                    toast={toast}
                    message="There was an error renewing your membership."
                    subMessage={errorMessage}
                />
            )
        })
    },
}
